import React, { useMemo } from 'react';
import styled from 'styled-components';
import {
  Avatar,
  BillableIcon,
  Currency,
  Icon,
  ListView,
  ListViewActions,
  ListViewSelection,
  Page,
  Tag,
  Tooltip,
} from '~/components';
import { BulkActionMenu } from '~/components/bulk-action';
import { useConfirmation } from '~/contexts';
import { useFeatures, useRowSelection } from '~/hooks';
import { colors, weights } from '~/styles';
import { sortAlphabetically } from '~/utils';
import BulkDelete from './BulkDelete';
import ProjectMemberActionsMenu from './ProjectMemberActionsMenu';
import SettingsActionsBanner from '../../SettingsActionsBanner';

const MemberName = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 0.5rem;
`;

const Indicators = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 0.75rem;
`;

const RoleName = styled.span`
  color: ${({ $inactive }) => ($inactive ? colors.grey40 : 'inherit')};
`;

const RolesTooltipTitle = styled.p`
  color: ${colors.grey40};
  font-size: 0.75rem;
  font-weight: ${weights.black};
  letter-spacing: 0.0625rem;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
  margin-left: 0.25rem;
`;

function ProjectMemberRoles({ roles }) {
  if (roles.length === 0) return null;

  const ordered = sortAlphabetically(roles, [(pmr) => (pmr.role.isActive ? 0 : 1), 'role.name']);
  const [first] = ordered;

  return (
    <>
      <RoleName $inactive={!first.role.isActive}>{first.role.name}</RoleName>

      {ordered.length > 1 && (
        <Tooltip
          message={
            <div style={{ fontSize: '1rem' }}>
              <RolesTooltipTitle>Roles</RolesTooltipTitle>

              {ordered.map((pmr) => (
                <Tag style={{ backgroundColor: colors.grey5 }} key={pmr.id}>
                  <small>
                    <RoleName $inactive={!pmr.role.isActive}>{pmr.role.name}</RoleName>
                  </small>
                </Tag>
              ))}
            </div>
          }>
          <Tag style={{ backgroundColor: colors.grey5, color: colors.grey40 }}>
            <small>+{roles.length - 1}</small>
          </Tag>
        </Tooltip>
      )}
    </>
  );
}

export default function ProjectMembersList({ project, data, members, onRefetch }) {
  const confirmation = useConfirmation();
  const features = useFeatures();

  const rows = useMemo(() => data ?? [], [data]);
  const selection = useRowSelection({ rows });

  const showBillable = !project.useRoles && project.isBillable;
  // Anyone who can edit the project can view rates — same implicit access the edit drawer granted.
  const showRate = showBillable;
  // Counted over the whole team, not the filtered rows — an admin hidden by the status filter still counts,
  // and the last-admin gating must not change with the filter. Only active admins count, because that is the
  // server's guard: an admin can be demoted so long as another active admin remains.
  const activeAdminCount = useMemo(
    () =>
      (members ?? []).filter((projectMember) => projectMember.typeId === 'administrator' && projectMember.isActive)
        .length,
    [members],
  );

  const handleBulkDelete = async () => {
    await confirmation.prompt((resolve) => (
      <BulkDelete
        projectId={project.id}
        ids={selection.ids}
        onComplete={async () => {
          selection.clear();
          await onRefetch();
        }}
        onClose={resolve}
      />
    ));
  };

  return (
    <>
      <Page.ListView>
        <ListView>
          <ListView.Header>
            <ListViewSelection.Column
              testId="project_members_bulk_checkbox"
              selection={selection}
              isVisible={project.permissions.edit}
              disabled={!rows.length}
            />
            <ListView.Column minWidth="16rem">Member</ListView.Column>
            <ListView.Column minWidth="10rem">Job Title</ListView.Column>
            <ListView.Column minWidth="10rem" isVisible={features.practices}>
              Practice
            </ListView.Column>
            <ListView.Column minWidth="10rem">Location</ListView.Column>
            <ListView.Column minWidth="14rem" isVisible={project.useRoles}>
              Roles
            </ListView.Column>
            <ListView.Column isVisible={showBillable} width="8rem" align="center">
              Billable
            </ListView.Column>
            <ListView.Column isVisible={showRate} width="10rem" align="right">
              Bill Rate
            </ListView.Column>
            <ListViewActions.Column isVisible={project.permissions.edit} />
          </ListView.Header>

          <ListView.Body>
            {rows.map((projectMember) => {
              const {
                id: projectMemberId,
                member,
                isBillable,
                typeId,
                rate,
                overrideCostRate,
                isActuallyActive,
              } = projectMember;
              const isActuallyBillable = project.isBillable && isBillable;
              const isAdmin = typeId === 'administrator';

              const warnings = [];
              if (project.isBillable && !member.isBillable) {
                warnings.push(`This is a non-billable workspace member that can't track time to billable projects.`);
              }
              if (project.permissions.viewMargin && overrideCostRate) {
                warnings.push('This member has an overridden cost rate on this project.');
              }

              return (
                <ListView.Row data-testid="row" key={projectMemberId} isDisabled={!isActuallyActive}>
                  <ListViewSelection.Cell id={projectMemberId} selection={selection} testId="checkbox_project_member" />
                  <ListView.Cell>
                    <Avatar value={member} isCircle hasBackground initialsFontSize=".9rem" />
                    <MemberName>
                      <div>{member.name}</div>
                    </MemberName>
                    <Indicators>
                      {isAdmin && (
                        <Tooltip message="Project Admin">
                          <Icon icon="user-gear" color={colors.primary} />
                        </Tooltip>
                      )}
                      {warnings.length > 0 && (
                        <Tooltip
                          maxWidth="22rem"
                          placement="right"
                          message={
                            <>
                              <p>The following exceptions exist for this member:</p>
                              <ul style={{ listStyle: 'disc inside', marginTop: '0.5rem' }}>
                                {warnings.map((warning) => (
                                  <li key={warning}>{warning}</li>
                                ))}
                              </ul>
                            </>
                          }>
                          <Icon icon="exclamation-triangle" color={colors.warning} />
                        </Tooltip>
                      )}
                    </Indicators>
                  </ListView.Cell>
                  <ListView.Cell>{member.jobTitle?.name}</ListView.Cell>
                  <ListView.Cell>{member.practice?.name}</ListView.Cell>
                  <ListView.Cell>{member.location?.name}</ListView.Cell>
                  <ListView.Cell>
                    <ProjectMemberRoles roles={projectMember.roles ?? []} />
                  </ListView.Cell>
                  <ListView.Cell>
                    <BillableIcon value={isActuallyBillable} />
                  </ListView.Cell>
                  <ListView.Cell>
                    {/* A stored rate means nothing while the member isn't billable here, so the cell stays
                        blank rather than quoting a number the project will never bill. */}
                    {!isActuallyBillable || rate == null ? null : (
                      <Currency value={rate} maximumFractionDigits={7} currency={project.currency} />
                    )}
                  </ListView.Cell>
                  <ListViewActions isVisible={project.permissions.edit}>
                    <ProjectMemberActionsMenu
                      variant="list"
                      project={project}
                      projectMember={projectMember}
                      activeAdminCount={activeAdminCount}
                      onChange={onRefetch}
                    />
                  </ListViewActions>
                </ListView.Row>
              );
            })}

            {rows.length === 0 && <ListView.Empty />}
          </ListView.Body>

          <ListView.Status total={rows.length} label="Team Member" />
        </ListView>
      </Page.ListView>

      {selection.any && (
        <SettingsActionsBanner>
          <BulkActionMenu count={selection.count}>
            {({ setIsOpen }) => (
              <BulkActionMenu.Item
                onClick={() => {
                  setIsOpen(false);
                  handleBulkDelete();
                }}>
                Remove
              </BulkActionMenu.Item>
            )}
          </BulkActionMenu>
        </SettingsActionsBanner>
      )}
    </>
  );
}
